// client/src/contexts/RecurringTransactionContext.js
import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import useAuthReset from '../hooks/useAuthReset';

const RecurringTransactionContext = createContext();

export const useRecurringTransactions = () => {
    const context = useContext(RecurringTransactionContext);
    if (!context) {
        throw new Error('useRecurringTransactions must be used within RecurringTransactionProvider');
    }
    return context;
};

// API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://soldikeeper-backend-production.up.railway.app/api';

// Helper to create authenticated API instance
const createAuthApi = () => {
    const token = localStorage.getItem('token');
    
    if (!token) {
        throw new Error('No authentication token found');
    }
    
    return axios.create({
        baseURL: API_BASE_URL,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-auth-token': token
        }
    });
};

export const RecurringTransactionProvider = ({ children }) => {
    const { user } = useAuth();
    const [recurringTransactions, setRecurringTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Privacy guard: clear cached recurring-transaction list when the
    // authenticated user changes (logout, account swap, forced 401).
    // Otherwise User A's templates would briefly remain visible to
    // User B on a shared device.
    const resetRecurringState = useCallback(() => {
        setRecurringTransactions([]);
        setError(null);
    }, []);
    useAuthReset(resetRecurringState);

    // Fetch all recurring transactions
    const fetchRecurringTransactions = useCallback(async () => {
        if (!user?.id) return;

        try {
            setLoading(true);
            setError(null);
            const api = createAuthApi();
            const response = await api.get('/recurring-transactions');

            if (response.data.success) {
                setRecurringTransactions(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching recurring transactions:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [user?.id]);

    // Create a new recurring transaction
    const createRecurringTransaction = useCallback(async (transactionData) => {
        try {
            setLoading(true);
            setError(null);
            const api = createAuthApi();
            const response = await api.post('/recurring-transactions', transactionData);

            if (response.data.success) {
                await fetchRecurringTransactions(); // Refresh list
                return response.data.data;
            }
        } catch (err) {
            console.error('Error creating recurring transaction:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [fetchRecurringTransactions]);

    // Update a recurring transaction
    const updateRecurringTransaction = useCallback(async (id, updates) => {
        try {
            setLoading(true);
            setError(null);
            const api = createAuthApi();
            const response = await api.put(`/recurring-transactions/${id}`, updates);

            if (response.data.success) {
                await fetchRecurringTransactions(); // Refresh list
                return response.data.data;
            }
        } catch (err) {
            console.error('Error updating recurring transaction:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [fetchRecurringTransactions]);

    // Cancel a recurring transaction
    const cancelRecurringTransaction = useCallback(async (id) => {
        try {
            setLoading(true);
            setError(null);
            const api = createAuthApi();
            const response = await api.post(`/recurring-transactions/${id}/cancel`);

            if (response.data.success) {
                await fetchRecurringTransactions(); // Refresh list
                return response.data.data;
            }
        } catch (err) {
            console.error('Error cancelling recurring transaction:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [fetchRecurringTransactions]);

    // Delete a recurring transaction
    const deleteRecurringTransaction = useCallback(async (id, deleteInstances = false) => {
        try {
            setLoading(true);
            setError(null);
            const api = createAuthApi();
            const response = await api.delete(`/recurring-transactions/${id}?deleteInstances=${deleteInstances}`);

            if (response.data.success) {
                await fetchRecurringTransactions(); // Refresh list
                return response.data;
            }
        } catch (err) {
            console.error('Error deleting recurring transaction:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [fetchRecurringTransactions]);

    // Get instances of a recurring transaction
    const getRecurringInstances = useCallback(async (id) => {
        try {
            const api = createAuthApi();
            const response = await api.get(`/recurring-transactions/${id}/instances`);

            if (response.data.success) {
                return response.data.data;
            }
            return [];
        } catch (err) {
            console.error('Error fetching recurring instances:', err);
            return [];
        }
    }, []);

    // Preview future occurrences
    const previewRecurring = useCallback(async (transactionData, months = 12) => {
        try {
            const api = createAuthApi();
            const response = await api.post('/recurring-transactions/preview', {
                ...transactionData,
                months
            });

            if (response.data.success) {
                return response.data.data;
            }
            return [];
        } catch (err) {
            console.error('Error generating preview:', err);
            return [];
        }
    }, []);

    // Process recurring transactions manually
    const processRecurringTransactions = useCallback(async () => {
        try {
            setLoading(true);
            const api = createAuthApi();
            const response = await api.post('/recurring-transactions/process');

            if (response.data.success) {
                await fetchRecurringTransactions(); // Refresh list
                return response.data.data;
            }
        } catch (err) {
            console.error('Error processing recurring transactions:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [fetchRecurringTransactions]);

    const value = {
        recurringTransactions,
        loading,
        error,
        fetchRecurringTransactions,
        createRecurringTransaction,
        updateRecurringTransaction,
        cancelRecurringTransaction,
        deleteRecurringTransaction,
        getRecurringInstances,
        previewRecurring,
        processRecurringTransactions
    };

    return (
        <RecurringTransactionContext.Provider value={value}>
            {children}
        </RecurringTransactionContext.Provider>
    );
};
